import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import ReactSelect, { ActionMeta, components, MenuPlacement, OptionProps, StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import cn from 'classnames';

import { ChipsSelectMenuPlacement } from '@components/common';
import GroupHeading from '@components/common/Select/components/GroupHeading';

import { useClickOutsideForFavoriteSelect } from '@utils/select/useClickOutsideForFavoriteSelect';
import prepareSelectStyles from './helpers/prepareSelectStyles';

import { TTooltipPlacement } from '@interfaces/components/componentTypes';
import { TChipsSelectMenuPlacementValues } from '../GroupSelect/GroupSelect.types';
import { EActionMeta, TSelectOptionType } from './Select.types';

import { EMPTY_LABEL, selectAllOption } from '@components/common/Select/constants';

import ClearIndicator from './components/ClearIndicator';
import DropdownIndicator from './components/DropdownIndicator';
import FormatGroupLabel from './components/FormatGroupLabel';
import Option from './components/Option';
import OptionFavorite from './components/OptionFavorite';
import SingleValue from './components/SingleValue';
import ValueContainer from './components/ValueContainer';
import Label from '../Label';
import Meta from '../Meta';
import Chips from './Chips';

import './Select.scss';

export type THandleChange = ((option: TSelectOptionType<any, any>, _e?: any) => void)
	| ((option: TSelectOptionType<any>[], _e?: any) => void)

export type SelectOptionProps = OptionProps<TSelectOptionType, true> & {
	data: TSelectOptionType[];
};

export interface ISelectPropsBase {
	backspaceRemovesValue?: boolean;
	className?: string;
	closeMenuOnSelect?: boolean;
	customComponents?: { [x: string]: React.ComponentType<any> };
	customStyles?: StylesConfig;
	disabled?: boolean;
	editTooltip?: string;
	errorMessage?: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
	favoriteTooltip?: string;
	handleChange?: THandleChange;
	hideSelectedOptions?: boolean;
	isClearable?: boolean;
	isCreatable?: boolean;
	isEditable?: boolean;
	isError?: boolean;
	isFavorite?: boolean;
	isFilter?: boolean;
	isFilterStyle?: boolean;
	isFitContent?: boolean;
	isLoading?: boolean;
	isMulti?: boolean;
	isOverflowLabelText?: boolean;
	isPagination?: boolean;
	isReadOnly?: boolean;
	isRenamable?: boolean;
	isSearchable?: boolean;
	isActionsDropdown?: boolean;
	label?: string;
	labelClassName?: string;
	maxMenuChipsHeight?: number;
	maxMenuHeight?: number;
	maxWidth?: string;
	menuChipsWidth?: number;
	menuIsOpen?: boolean;
	menuPlacement?: MenuPlacement;
	menuPlacementChips?: TChipsSelectMenuPlacementValues;
	menuPortalTarget?: HTMLElement | null;
	minHeight?: string;
	minWidth?: string;
	multiselectText?: string;
	name: string;
	noOptionsMessage?: string;
	onBlur?: () => void;
	onDeleteClick?: THandleChange;
	onEditClick?: THandleChange;
	onMenuClose?: () => void;
	onMenuOpen?: () => void;
	onMenuScrollToBottomCallback?: (event: WheelEvent | TouchEvent) => void;
	onRenameClick?: THandleChange;
	optionMaxWidth?: number;
	options?: Array<TSelectOptionType>;
	placeholder?: string;
	renameTooltip?: string;
	required?: boolean;
	setFavorite?: (data: TSelectOptionType) => void;
	testId?: string;
	tooltipContent?: JSX.Element | string;
	tooltipMaxWidth?: number;
	tooltipPlacement?: TTooltipPlacement;
	units?: string;
	validateNewOption?: (inputValue: string) => boolean;
	value?: TSelectOptionType | Array<TSelectOptionType> | null | unknown;
	width?: string;
	withFixedValue?: boolean;
	isWithAsterisk?: boolean;
	isWithoutBorder?: boolean;
	initialGroupLabels?: string[];
	emptyGroupText?: string;
	singleOpenGroup?: boolean;
}

export type ISelectProps =
	| (ISelectPropsBase & { isWithoutUseForms: true; control?: undefined })
	| (ISelectPropsBase & { isWithoutUseForms?: false; control: Control<any> });

const Select = ({
	backspaceRemovesValue = false,
	className,
	closeMenuOnSelect = true,
	control,
	customComponents = {},
	customStyles,
	disabled,
	editTooltip,
	errorMessage,
	favoriteTooltip,
	handleChange,
	hideSelectedOptions = false,
	isClearable,
	isCreatable = false,
	isEditable = false,
	isError,
	isFavorite = false,
	isFilterStyle = false,
	isFitContent = false,
	isLoading,
	isMulti = false,
	isOverflowLabelText,
	isPagination = false,
	isReadOnly = false,
	isRenamable = false,
	isSearchable,
	isActionsDropdown = false,
	isWithoutUseForms = false,
	label,
	labelClassName,
	maxMenuChipsHeight = 300,
	maxMenuHeight = 300,
	maxWidth,
	menuChipsWidth,
	menuIsOpen,
	menuPlacement,
	menuPlacementChips = ChipsSelectMenuPlacement.bottom,
	menuPortalTarget,
	minHeight,
	minWidth,
	multiselectText = 'items selected',
	name,
	noOptionsMessage = 'No options',
	onBlur,
	onDeleteClick,
	onEditClick,
	onMenuClose,
	onMenuOpen,
	onMenuScrollToBottomCallback,
	onRenameClick,
	optionMaxWidth,
	options = [],
	placeholder,
	renameTooltip,
	required,
	setFavorite,
	testId,
	tooltipContent,
	tooltipMaxWidth,
	tooltipPlacement,
	units,
	validateNewOption,
	value = null,
	width,
	withFixedValue = false,
	isWithAsterisk = true,
	isWithoutBorder = false,
	initialGroupLabels = [],
	emptyGroupText = 'Empty group',
	singleOpenGroup = false,
}: ISelectProps) => {
	const [isOpen, setIsOpen] = useState(!!menuIsOpen);
	const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
	const [allGroupLabels, setAllGroupLabels] = useState<string[]>([]);
	const [openActionsDropdownId, setOpenActionsDropdownId] = useState<string | null>(null);

	const isGroups = useMemo(
		() => initialGroupLabels.length > 0 || options.some((item) => !!item?.groupLabel),
		[options, initialGroupLabels],
	);
	const isWithoutCustomHandlers = isRenamable || isEditable || isFavorite || isWithoutUseForms;

	const SelectComponent = isCreatable ? CreatableSelect : ReactSelect;

	const selectStyles = useMemo(
		() =>
			prepareSelectStyles(
				isError,
				width,
				minWidth,
				maxWidth,
				isFilterStyle,
				isPagination,
				isEditable,
				isRenamable,
				isFavorite,
				isMulti,
				isReadOnly,
				isActionsDropdown,
				minHeight,
				isWithoutBorder,
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			isError,
			width,
			minWidth,
			maxWidth,
			isFilterStyle,
			isRenamable,
			isEditable,
			isFavorite,
			isMulti,
			isReadOnly,
			isActionsDropdown,
		],
	);
	const selectRef = useRef(null);
	const containerRef = useRef(null);
	const multiSelectWrapperRef = useRef(null);

	const customChangeHandler = useCallback((
		selectedValue: Array<TSelectOptionType>,
		actionMeta: ActionMeta<TSelectOptionType>,
	) => {
		const isAllOption = actionMeta?.option?.value === selectAllOption?.value;

		if (!isMulti || isAllOption) return;

		switch (actionMeta.action) {
			case EActionMeta.removeValue:
			case EActionMeta.popValue:
				if (!actionMeta.removedValue?.isFixed && handleChange) {
					handleChange(
						selectedValue.filter((selectedItem) => selectedItem?.value !== actionMeta.removedValue?.value),
						actionMeta.action,
					);
				}
				break;
			case EActionMeta.clear:
				if (handleChange)
					handleChange(
						actionMeta.removedValues.filter((value) => value?.isFixed || value?.disabled),
						actionMeta.action,
					);
				break;
			case EActionMeta.deselectOption:
				if (!actionMeta.option?.isFixed && handleChange) {
					handleChange(
						selectedValue.filter((selectedItem) => selectedItem?.value !== actionMeta.option?.value),
						actionMeta.action,
					);
				}
				break;
			case EActionMeta.selectOption:
				if (!actionMeta.option?.isFixed && handleChange) {
					handleChange(selectedValue, actionMeta.action);
				}
				break;
			default:
				if (handleChange) handleChange(selectedValue, actionMeta.action);
		}
	}, [handleChange, isMulti]);

	const removeChipsItem = (removedValue: TSelectOptionType) => {
		if (handleChange && (!removedValue?.isFixed || !removedValue?.disabled)) {
			handleChange((value as Array<TSelectOptionType>)
				?.filter((option) => option?.value !== removedValue?.value));
		}
	};

	const handleMenuOpen = () => {
		if (isOpen || isWithoutCustomHandlers || disabled) return;

		setIsOpen(true);
	};

	const handleMenuClose = useCallback(() => {
		if (isWithoutCustomHandlers) return;

		setIsOpen(false);

		if (onBlur) onBlur();
	}, [isWithoutCustomHandlers, onBlur]);

	const handleFocus = () => {
		if (isWithoutCustomHandlers) return;

		if (selectRef.current) {
			(selectRef as any).current.focus();
		}
	};

	const onMenuScrollToBottom = (event: WheelEvent | TouchEvent) => {
		if (onMenuScrollToBottomCallback) onMenuScrollToBottomCallback(event);
	};

	const getNoOptionsMessage = () => <div>{noOptionsMessage}</div>;

	const handleToggleActionsDropdown = (id: string | null) => {
		setOpenActionsDropdownId((prevState) => (prevState === id ? null : id));
	};

	const selectComponents = useMemo(() => {
		return {
			...components,
			DropdownIndicator,
			ClearIndicator: (props: any) => ClearIndicator(props, isFilterStyle),
			SingleValue: (props: any) => {
				return SingleValue(props, isFilterStyle, placeholder, isWithAsterisk);
			},
			GroupHeading: (props: any) => GroupHeading({
				props,
				collapsedGroups,
				setCollapsedGroups,
				allGroupLabels,
				singleOpenGroup,
			}),
			Option: (props: any) =>
				isRenamable || isEditable
					? Option({
						props,
						onRenameClick,
						onEditClick,
						onDeleteClick,
						handleChange,
						maxWidth: optionMaxWidth,
						renameTooltip,
						editTooltip,
						isRenamable,
						isEditable,
						isGroups,
						collapsedGroups,
						isActionsDropdown,
						openActionsDropdownId,
						handleToggleActionsDropdown,
					})
					: isFavorite
						? OptionFavorite(props, setFavorite, favoriteTooltip)
						: options.some((item) => item?.subText) || isMulti
							? Option({
								props,
								handleChange,
								maxWidth: optionMaxWidth,
								renameTooltip,
								editTooltip,
								isRenamable,
								isEditable,
								isGroups,
								collapsedGroups,
								isActionsDropdown,
								openActionsDropdownId,
								handleToggleActionsDropdown,
							})
							: components.Option(props),
			ValueContainer: (props: any) =>
				isMulti
					? ValueContainer(
						props,
						multiselectText,
						handleMenuClose,
						multiSelectWrapperRef,
						isFilterStyle,
					)
					: components.ValueContainer(props),
			MultiValue: () => null,
			...customComponents,
		};
	}, [
		collapsedGroups,
		customComponents,
		editTooltip,
		favoriteTooltip,
		handleChange,
		handleMenuClose,
		isActionsDropdown,
		isEditable,
		isFavorite,
		isFilterStyle,
		isGroups,
		isMulti,
		isRenamable,
		multiselectText,
		onDeleteClick,
		onEditClick,
		onRenameClick,
		openActionsDropdownId,
		optionMaxWidth,
		options,
		placeholder,
		renameTooltip,
		setFavorite,
		isWithAsterisk,
		allGroupLabels,
		singleOpenGroup,
	]);

	const preparedOptions = useMemo(() => {
		if (!isGroups) return options;

		const groupsMap = new Map<string, TSelectOptionType[]>();
		const ungrouped: TSelectOptionType[] = [];

		options.forEach((item) => {
			if (item?.groupLabel) {
				const list = groupsMap.get(item.groupLabel) || [];
				groupsMap.set(item.groupLabel, [...list, item]);
			} else {
				ungrouped.push(item);
			}
		});

		const discovered = Array.from(groupsMap.keys()).filter((l) => !initialGroupLabels.includes(l));
		const orderedLabels = [...initialGroupLabels, ...discovered];

		const grouped = orderedLabels.map((label, index) => {
			const list = groupsMap.get(label) || [];

			const finalOptions: TSelectOptionType[] =
				list.length > 0
					? list
					: [{
						value: `${EMPTY_LABEL}${label}`,
						label: emptyGroupText,
						disabled: true,
						isPlaceholder: true,
						groupLabel: label,
					}];

			return { label, options: finalOptions, groupIndex: index };
		});

		return [...grouped, ...ungrouped];
	}, [options, isGroups, initialGroupLabels, emptyGroupText]);

	const selectOptions = useMemo(() => {
		return isMulti && preparedOptions.length && !isFilterStyle
			? [selectAllOption, ...preparedOptions]
			: preparedOptions;
	}, [isFilterStyle, isMulti, preparedOptions]);

	const selectStylesMemoized = useMemo(() => {
		return {
			...selectStyles,
			...(customStyles || {}),
			menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
		};
	}, [customStyles, selectStyles]);

	const onChange = useCallback((
		selectedValue: unknown,
		actionMeta: ActionMeta<unknown>,
	) => {
		const castedValue = selectedValue as Array<TSelectOptionType>;

		if (withFixedValue || isMulti) {
			customChangeHandler(castedValue, actionMeta as ActionMeta<TSelectOptionType>);
		} else {
			if (handleChange) handleChange(castedValue);
		}
	}, [
		customChangeHandler,
		handleChange,
		isMulti,
		withFixedValue,
	]);

	useEffect(() => {
		if (!isGroups) {
			return;
		}

		const uniqueGroupLabels: string[] = [];

		for (const item of options){
			const label = item?.groupLabel;

			if (label && !uniqueGroupLabels.includes(label)) {
				uniqueGroupLabels.push(label);
			}
		}

		const totalGroups = Array.from(
			new Set([...(initialGroupLabels || []), ...uniqueGroupLabels]),
		);

		setAllGroupLabels(totalGroups);

		setCollapsedGroups((prevState) => {
			if (prevState.length) {
				return prevState;
			}
				return totalGroups;

		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isGroups, options?.length]);

	useClickOutsideForFavoriteSelect({
		isFavorite,
		multiSelectWrapperRef,
		selectRef,
		onMenuClose,
	});

	return (
		<div className='f5gSelectFormWrapper'>
			<div
				ref={containerRef}
				className={cn('f5gSelectContainer', className, { 'fitContent': isFitContent })}
				onClick={handleFocus}
			>
				{label && (
					<Label
						className={labelClassName}
						isOverflowLabelText={isOverflowLabelText}
						label={label}
						required={required}
						tooltipContent={tooltipContent}
						tooltipMaxWidth={tooltipMaxWidth}
						tooltipPlacement={tooltipPlacement}
					/>
				)}
				{isWithoutUseForms || isEditable || isFavorite || isMulti ? (
					<div
						ref={multiSelectWrapperRef}
						data-testid={testId || 'multi-select-wrapper'}
						onClick={handleMenuOpen}
					>
						<SelectComponent
							ref={selectRef}
							aria-label={name}
							backspaceRemovesValue={backspaceRemovesValue}
							classNames={{
								menuPortal: () => 'f5gFavoriteSelectEditButtonsContainer',
								menuList: () => 'f5gSelectMenuList',
							}}
							closeMenuOnSelect={closeMenuOnSelect}
							components={selectComponents}
							formatGroupLabel={isGroups ? undefined : (group) =>
								FormatGroupLabel({
									group,
									handleChange,
									value: value as Array<TSelectOptionType>,
								})
							}
							hideSelectedOptions={hideSelectedOptions}
							isClearable={isClearable}
							isDisabled={disabled || isReadOnly}
							isLoading={isLoading}
							isMulti={isMulti}
							isOptionDisabled={(option) => (option as { disabled: boolean })?.disabled}
							isSearchable={isSearchable}
							maxMenuHeight={maxMenuHeight}
							menuIsOpen={isWithoutCustomHandlers ? menuIsOpen : isOpen}
							menuPlacement={menuPlacement}
							menuPortalTarget={menuPortalTarget}
							noOptionsMessage={getNoOptionsMessage}
							onBlur={onBlur}
							onChange={onChange}
							onMenuScrollToBottom={onMenuScrollToBottom}
							options={selectOptions}
							placeholder={placeholder}
							styles={selectStylesMemoized}
							value={value}
						/>
					</div>
				) : (
					<Controller
						control={control}
						name={name}
						render={(props: { field: any }): any => {
							return (
								<div data-testid={testId || 'multi-select-wrapper'}>
									<SelectComponent
										ref={selectRef}
										aria-label={name}
										backspaceRemovesValue={backspaceRemovesValue}
										closeMenuOnSelect={closeMenuOnSelect}
										components={{
											...components,
											DropdownIndicator,
											ClearIndicator: (props) => ClearIndicator(props, isFilterStyle),
											...[options.some((item) => !!(item?.subText || item?.tooltipText))
												? {
													Option: (props: any) => Option(props),
												}
												: {},
											],
											...customComponents,
										}}
										hideSelectedOptions={hideSelectedOptions}
										isClearable={isClearable}
										isDisabled={disabled}
										isLoading={isLoading}
										isMulti={false}
										isOptionDisabled={(option) => (option as { disabled: boolean })?.disabled}
										isSearchable={isSearchable}
										isValidNewOption={validateNewOption}
										maxMenuHeight={maxMenuHeight}
										menuPlacement={menuPlacement}
										noOptionsMessage={getNoOptionsMessage}
										onMenuClose={onMenuClose}
										onMenuOpen={onMenuOpen}
										onMenuScrollToBottom={onMenuScrollToBottom}
										options={preparedOptions}
										placeholder={placeholder}
										styles={{ ...selectStyles, ...(customStyles || {}) }}
										{...props.field}
										classNames={{
											menuList: () => 'f5gSelectMenuList',
										}}
										onChange={(withFixedValue ? customChangeHandler : handleChange)
											|| props.field.onChange
										}
									/>
								</div>
							);
						}}
					/>
				)}
				{isMulti && !isFilterStyle && !!(value as Array<TSelectOptionType>)?.length && (
					<Chips
						containerRef={containerRef}
						maxMenuChipsHeight={maxMenuChipsHeight}
						menuChipsWidth={menuChipsWidth}
						menuPlacement={menuPlacementChips}
						readOnly={disabled}
						removeOption={removeChipsItem}
						selectedListOfOptions={value as Array<TSelectOptionType>}
						value={value as Array<TSelectOptionType>}
					/>
				)}
				{units && <span className='f5gSelectUnits'>{units}</span>}
			</div>
			{errorMessage && <Meta message={errorMessage as string}/>}
		</div>
	);
};

export default memo(Select);
